import merge from "lodash/merge";

type ConfigOption<Create extends (input: any) => any> = {
  create: Create;
};

type ConfigOptionInput<O extends ConfigOption<any>> = O extends ConfigOption<
  infer F
>
  ? Parameters<F>[0]
  : never;
type ConfigOptionValue<O extends ConfigOption<any>> = O extends ConfigOption<
  infer F
>
  ? ReturnType<F>
  : never;

export const createConfigOption = <Value>(): ConfigOption<
  (v: Value) => Value
> => {
  return { create: (input) => input };
};

export const map =
  <NewInput>() =>
  <Value, ExistingInput>(option: ConfigOption<(e: ExistingInput) => Value>) =>
  (
    mapFn: (input: NewInput) => ExistingInput
  ): ConfigOption<(n: NewInput) => Value> => {
    return {
      create: (input) => {
        return option.create(mapFn(input));
      },
    };
  };

export const optionalWithDefault =
  <Value, Input>(option: ConfigOption<(i: Input) => Value>) =>
  (defaultValue: Input): ConfigOption<(i?: Input) => Value> => {
    return {
      create: (input = undefined) => {
        return option.create(input ?? defaultValue);
      },
    };
  };

export const struct = <Options extends Record<string, ConfigOption<any>>>(
  options: Options
): ConfigOption<
  (
    i: NullableValuesToOptional<
      { [K in keyof Options]: ConfigOptionInput<Options[K]> }
    >
  ) => { [K in keyof Options]: ConfigOptionValue<Options[K]> }
> => {
  return {
    create: (input) => {
      return Object.entries(options)
        .map(
          ([key, option]) =>
            [key, option] as [keyof typeof options, typeof option]
        )
        .reduce((acc, [key, option]) => {
          return {
            ...acc,
            [key]: option.create((input as any)[key]),
          };
        }, {} as { [K in keyof Options]: ConfigOptionValue<Options[K]> });
    },
  };
};

export const record =
  <Key extends string = string>() =>
  <Option extends ConfigOption<any>>(
    option: Option
  ): ConfigOption<
    (
      i: Record<Key, ConfigOptionInput<Option>>
    ) => Record<Key, ConfigOptionValue<Option>>
  > => {
    return {
      create: (input) => {
        return Object.entries(input).reduce((acc, [key, value]) => {
          return {
            ...acc,
            [key]: option.create(value),
          };
        }, {} as Record<Key, ConfigOptionValue<Option>>);
      },
    };
  };

export const partialWithDefault =
  <Option extends ConfigOption<any>>(option: Option) =>
  (
    defaults: ConfigOptionInput<Option>
  ): ConfigOption<
    (i: Partial<ConfigOptionInput<Option>>) => ConfigOptionValue<Option>
  > => {
    return {
      create: (input) => {
        const merged = merge({}, defaults, input);
        return option.create(merged);
      },
    };
  };

type NonNull<T> = T extends undefined ? never : T extends null ? never : T;
type NullableKeys<T> = NonNullable<
  {
    [K in keyof T]: T[K] extends NonNull<T[K]> ? never : K;
  }[keyof T]
>;

type NullableValuesToOptional<T> = Omit<T, NullableKeys<T>> &
  Partial<Pick<T, NullableKeys<T>>>;
